import React, { useEffect, useState } from 'react';
import { SkeletonLoader } from '../../components/loading/SkeletonLoader';
import AssignmentIcon from '@material-ui/icons/Assignment';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import ScheduleIcon from '@material-ui/icons/Schedule';
import FlagIcon from '@material-ui/icons/Flag';
import WarningIcon from '@material-ui/icons/Warning';
import { retrieveTaskStatusData, TaskStatus, TaskData, TaskStatusEnum, TaskStatusQueryParams } from '../TasksSummaryRepository';
import { showNotification } from '../../App';
import { TaskSummaryAdvancedFilter } from '../TasksSummaryPage';
import { IAutoCompleteItem } from '../../typings/autoComplete';

const CHIP_COLORS = [
    { backgroundColor: '#e3f2fd', color: '#1565c0', border: '1px solid #bbdefb' },
    { backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' },
    { backgroundColor: '#fff8e1', color: '#b45309', border: '1px solid #fde68a' },
    { backgroundColor: '#f3e5f5', color: '#6a1b9a', border: '1px solid #e1bee7' },
    { backgroundColor: '#fce4ec', color: '#880e4f', border: '1px solid #f8bbd0' },
];

const CHIP_ICONS = [AssignmentIcon, PlayCircleOutlineIcon, ScheduleIcon, FlagIcon, WarningIcon];

const CHIP_STYLE_BASE = {
    display: 'inline-flex' as const,
    alignItems: 'center' as const,
    gap: 4,
    padding: '4px 10px',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 500,
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    whiteSpace: 'nowrap' as const,
};

interface StatusCardProps {
    filter: string;
    isRefreshData: boolean;
    isFormOpen: boolean;
    advancedFilter: TaskSummaryAdvancedFilter;
    isInclude: TaskStatusEnum | null;
    isIncludeCrmTasks: boolean | null;
    isMyTasksOnly: boolean | null;
    isPersonalTask: boolean | null;
    isFromDataSource: boolean | null;
    isIncludeFollowUpTasksOnly: boolean | null;
}

interface StatusCardState {
    statuses: TaskStatus[];
    isLoading: boolean;
}

export const StatusCard: React.FunctionComponent<StatusCardProps> = (props) => {

    const [state, setState] = useState<StatusCardState>({
        statuses: [],
        isLoading: true,
    });

    useEffect(() => {
        if (!props.isFormOpen) {
            fetchData();
        }
    // Only re-fetch when filter/display values change — not on unrelated parent re-renders
    // tslint:disable-next-line: align
    }, [props.filter, props.advancedFilter, props.isInclude, props.isIncludeCrmTasks, props.isMyTasksOnly,
        props.isPersonalTask, props.isFromDataSource, props.isRefreshData,
        props.isIncludeFollowUpTasksOnly, props.isFormOpen]);

    const fetchData = () => {

        setState(prev => ({ ...prev, isLoading: true }));

        const taskStatusQueryParams: TaskStatusQueryParams = {
            taskStatusType: 'USER',
            includeCount: true,
            offset: null,
            first: null,
            filter: props.filter || null,
            taskStatus: props.isInclude,
            isIncludeCrmTasks: props.isIncludeCrmTasks,
            isMyTasksOnly: props.isMyTasksOnly,
            groupBy: null,
            groupByValue: null,
            completedFromDate: props.advancedFilter.completedFrom ? props.advancedFilter.completedFrom : null,
            completedToDate: props.advancedFilter.completedTo ? props.advancedFilter.completedTo : null,
            updatedFromDate: props.advancedFilter.updatedFrom ? props.advancedFilter.updatedFrom : null,
            updatedToDate: props.advancedFilter.updatedTo ? props.advancedFilter.updatedTo : null,
            priorityList: convertArrayToStringFromat(props.advancedFilter.priority),
            statusList: convertArrayToStringFromat(props.advancedFilter.status),
            allocatedToList: convertArrayToStringFromat(props.advancedFilter.allocatedTo),
            allocatedByList: convertArrayToStringFromat(props.advancedFilter.allocatedBy),
            created: props.advancedFilter.created ? props.advancedFilter.created.value : null,
            due: props.advancedFilter.due ? props.advancedFilter.due.value : null,
            teamList: convertArrayToStringFromat(props.advancedFilter.team),
            taskTypes: convertArrayToStringFromat(props.advancedFilter.taskTypes),
            isPersonalTask: props.isPersonalTask,
            isFromDataSource: props.isFromDataSource,
            matterList: convertArrayToStringFromat(props.advancedFilter.matterList),
            isIncludeFollowUpTasksOnly: props.isIncludeFollowUpTasksOnly,
            isIncludeCount: true,
        };

        retrieveTaskStatusData(
            taskStatusQueryParams,
            true,
            (data) => onRetrieveStatus(data),
            // tslint:disable-next-line:no-any
            function (reason: any): void {
                setState(prev => ({ ...prev, isLoading: false }));
                showNotification(null, reason, 'error');
            }
        );
    };

    const convertArrayToStringFromat = (_values: IAutoCompleteItem[] | undefined) => {
        return _values && _values.length > 0 ? _values.map((value: IAutoCompleteItem) => value.value).join() : null;
    };

    const onRetrieveStatus = (data: TaskData) => {
        setState({
            isLoading: false,
            statuses: data.task.statuses.filter((x: TaskStatus) => {
                return x.description.toLowerCase() !== 'complete';
            }),
        });
    };

    if (state.isLoading) {
        const skeletonCount = state.statuses.length > 0 ? state.statuses.length : 4;
        return (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {Array.from({ length: skeletonCount }).map((_, i) => (
                    <div
                        key={i}
                        style={{
                            ...CHIP_STYLE_BASE,
                            backgroundColor: 'rgba(0,0,0,0.04)',
                            border: '1px solid rgba(0,0,0,0.06)',
                            gap: 6,
                        }}
                    >
                        <SkeletonLoader variant="shape" shape="circle" width={14} height={14} />
                        <SkeletonLoader variant="shape" shape="text" width={24} height={12} />
                        <SkeletonLoader variant="shape" shape="text" width={80} height={12} />
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {state.statuses.map((status: TaskStatus, index: number) => {
                const chipColor = CHIP_COLORS[index % CHIP_COLORS.length];
                const ChipIcon = CHIP_ICONS[index % CHIP_ICONS.length];
                return (
                    <div
                        key={status.description}
                        style={{ ...CHIP_STYLE_BASE, ...chipColor }}
                    >
                        <ChipIcon style={{ fontSize: 12 }} />
                        <strong style={{ fontWeight: 700 }}>{status.count}</strong>
                        <span>{status.description}</span>
                    </div>
                );
            })}
        </div>
    );
};
